import { useState, useId, useRef, useCallback } from 'react';
import { registerCodes } from '@norkron/web-errors';
import { registerLazy } from '@norkron/web-conformance/register-lazy';

// src/use-controlled-state.ts

// src/self-check.ts
var LIB_ID = "@norkron/web-controlled-state";
var FINDING_BUFFER = [];
function recordControlFlip(args) {
  if (FINDING_BUFFER.length >= 256) return;
  FINDING_BUFFER.push({
    libId: LIB_ID,
    code: "WCS_CONTROL_MODE_FLIP",
    category: "schema",
    severity: "major",
    message: `useControlledState flipped ${args.from} \u2192 ${args.to}; the controlled prop must remain defined or undefined for the lifetime of the component.`,
    suggestedFix: "Initialise the controlled prop to a non-undefined value on first render, or rely on the uncontrolled fallback by leaving it undefined for the whole component lifetime.",
    extras: { hookId: args.hookId, from: args.from, to: args.to }
  });
}
function selfCheck(_root, _opts) {
  if (FINDING_BUFFER.length === 0) return [];
  const out = FINDING_BUFFER.slice();
  FINDING_BUFFER.length = 0;
  return out;
}

// src/use-controlled-state.ts
function useControlledState(controlled, defaultValue, onChange) {
  const [internal, setInternal] = useState(defaultValue);
  const isControlled = controlled !== void 0;
  const hookId = useId();
  const prevIsControlledRef = useRef(isControlled);
  if (prevIsControlledRef.current !== isControlled) {
    recordControlFlip({
      from: prevIsControlledRef.current ? "controlled" : "uncontrolled",
      to: isControlled ? "controlled" : "uncontrolled",
      hookId
    });
    prevIsControlledRef.current = isControlled;
  }
  const value = isControlled ? controlled : internal;
  const setValue = useCallback(
    (next) => {
      if (!isControlled) setInternal(next);
      onChange?.(next);
    },
    [isControlled, onChange]
  );
  return { value, setValue, isControlled };
}
var WEB_CONTROLLED_STATE_LIB_ID = "@norkron/web-controlled-state";
try {
  registerCodes(WEB_CONTROLLED_STATE_LIB_ID, {
    WCS_CONTROL_MODE_FLIP: {
      description: "selfCheck / runtime: a useControlledState hook flipped between controlled (`value` prop provided) and uncontrolled (`value` undefined) modes across renders. React warns against this pattern because it desynchronises consumer + internal state. (Conformance severity: major.)",
      severity: "warning",
      kind: "contract",
      docsAnchor: "https://norkron.dev/web-controlled-state/WCS_CONTROL_MODE_FLIP",
      exampleFix: "Pick one mode and stick to it. If conditionally controlled, default the uncontrolled branch to an initial value (`value={controlledValue ?? defaultValue}`) so the prop is never undefined after first mount."
    }
  });
} catch {
}
registerLazy(
  "@norkron/web-controlled-state",
  () => selfCheck
);

export { WEB_CONTROLLED_STATE_LIB_ID, selfCheck, useControlledState };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map